/**
 * The run, in one row above the input: what a run would do before it starts,
 * what it is doing while it runs, and how it ended.
 *
 * A usability study counted the run's controls in four chips, four popovers, a
 * floating pod in the top-left corner and a done bar across the top of the field
 * — the press that starts a run at the bottom, the one that stops it at the top,
 * and the sentence saying how it ended somewhere else again. So the three phases
 * of one thing share one place, the composer's top row, and change in it:
 *
 *   AT REST  "Let it run · 12 moves · stops near $4 est. [Run] · Adjust". The
 *            count and the cap are the ones the dials set (the draft's /commands
 *            applied, as the press will apply them), and Adjust opens the four
 *            dials IN FLOW above the row — D22's rule that the dials never float
 *            over the map, on the desktop as well as the phone (D158). While an
 *            opening is going, Run is offered too, and holds the run until the
 *            opening lands: "Runs when the opening lands · 12 moves · est. $4
 *            [Cancel]" (D351). It is the one door to a run after Explore — the
 *            split press that chained them is gone.
 *   LIVE     "Move 5 of 12 · Deepen · <claim> [Details] [Stop]", the figure so
 *            far under the move. At an ask-first pause the gate joins the row on
 *            a line of its own: the word, the price, Go, Not there. The move's
 *            reason, the counts, the follow switch and Not there between pauses
 *            are one press away, under Details, above the row.
 *   DONE     The stop head, what the run found in counts ("2 held up · 3 still
 *            open"), and "What it found" on a line of their own, over the rest
 *            line — which is where Continue and Start over already live after a
 *            Stop or a provider that stopped answering (D337), so an ending adds a
 *            head rather than a second set of presses.
 *            The press opens the run's card in the thread, whose own last row is
 *            the account (D335). Its ✕ clears the ending; the coach's quiet line
 *            keeps its own place in the run-done column (D164).
 *
 * A STRIP in code, a row on screen: `RunRow` is already taken by one row of a
 * run's own feed (`core/runFeed.ts`), and the two-feeds trap in AGENTS.md is
 * what a second meaning under one name costs.
 *
 * One Stop, here. The composer's primary still stops a lone pass, the space bar
 * still stops a run, and Escape never does (D22): a reflex dismissal must not be
 * the press that ends something costing money.
 *
 * Every count on this row is something that happened or the move in flight.
 * There is no progress bar, because a fraction complete would claim there is a
 * defined amount to find; "Move 5 of 12" is a budget being spent. And every
 * dollar figure is an estimate that leans high and says "est." in the visible
 * string (invariant 5) — this row is watched for minutes at a time, which is
 * exactly when an unqualified figure would be read as a running bill.
 */

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react'
import { MOVE_REASON, OP_LABEL, RUN_STOP_HEAD, runProgress } from '../core/types'
import type { RunOpen } from '../core/types'
import { runFound, type FoundRow } from '../core/runFound'
import { openRunFound } from './RunFound'
import { PRICE_READ_ON, fmtUsd } from '../core/models'
import { useStore, type RunLive } from '../core/store'
import { FOUND_WORD, STATUS_WORD } from '../core/vocabulary'
import { runEndingsLine } from '../core/endings'
import { runPauseLine, runPauseSpoken, runPauseWhy, unreachableWhy } from '../core/failure'
import { approveMove, declineHere, stopRun } from '../engine/run'
import { auditorFor } from '../engine/reason'
import { Icon } from './components/Icon'
import { gatePriceVerb } from './actPress'
import { gateReady } from './runGate'
import { VerbPrice } from './VerbPrice'
import { setAdjustOpen } from './adjust'

/**
 * Where focus goes when one of the strip's own presses takes itself off screen.
 *
 * Run is replaced by the live row and Stop by the ending, so the button a key
 * had just pressed unmounted under it and focus fell to the page body: a screen
 * reader said nothing about where it was, and the next Tab began at the top of
 * the page. The press names the control that will replace it; the row that
 * mounts takes it, once, and only while focus really is lost and the press is
 * a moment old. A run that ends by itself, or that Space stopped from anywhere
 * else, moves nobody's focus.
 */
let seatNext: { at: number; on: 'stop' | 'account' } | null = null
const seatOn = (on: 'stop' | 'account') => (seatNext = { at: Date.now(), on })

function useSeat(on: 'stop' | 'account', ref: RefObject<HTMLButtonElement | null>) {
  useEffect(() => {
    const ask = seatNext
    if (!ask || ask.on !== on) return
    seatNext = null
    const here = document.activeElement
    if (Date.now() - ask.at > 10_000 || (here && here !== document.body)) return
    ref.current?.focus({ preventScroll: true })
  }, [on, ref])
}

/**
 * The palette's "Let it run": focus on this row's own press, and nothing started.
 *
 * The palette's row used to start its own run — always 24 moves, none of the
 * checks or panels set under Adjust, and no estimate, under the same name as the
 * strip's press that runs the dials and prints its figure. Two presses called
 * Let it run that bought two different runs, and only one of them priced (D338).
 * So the row brings the reader HERE: the run's press, or Continue after a run that
 * left moves, with its count and estimate beside it — and one ↵ more starts it.
 * Where the press cannot run (a cap under the floor, a key a dial needs), focus
 * takes Adjust instead, over the strip's line that says why; a disabled button
 * cannot hold focus, and a palette row that landed nowhere would be one more
 * silent refusal.
 *
 * An ask with a moment's lifetime, taken by whichever rest line is mounted —
 * the composer's, or the phone's once its sheet has closed and the composer has
 * come back. It waits for the palette's own close to finish: until then the stage
 * is `inert`, and the dialog's focus return would land after a focus given now.
 * Free: a focus. Never persisted.
 */
let runAsk = 0
const runTakers = new Set<() => boolean>()

function offerRunAsk(tries = 0): void {
  if (!runAsk || Date.now() - runAsk > 10_000) return
  for (const take of runTakers) {
    if (take()) {
      runAsk = 0
      return
    }
  }
  if (tries < 40) setTimeout(() => offerRunAsk(tries + 1), 50)
  // Two seconds with no press able to take it: the ask lapses rather than wait
  // for a rest line that mounts later and pull focus off whatever the reader has
  // gone on to type in.
  else runAsk = 0
}

/**
 * A held ↵ does not press a paid press it was not aimed at. The palette's Let it
 * run is chosen on a keydown and brings focus here a few frames later, and a key
 * held a moment too long repeats onto the button, which a repeated Enter
 * activates — a paid run with no press of its own, before its estimate was read.
 * Preventing the repeat prevents the click; a fresh ↵ still presses. The chain
 * button guards the same hazard (Composer).
 */
const noHeldPress = (e: KeyboardEvent<HTMLButtonElement>) => {
  if (e.repeat && (e.key === 'Enter' || e.key === ' ')) e.preventDefault()
}

export function focusRunPress(): void {
  runAsk = Date.now()
  setTimeout(offerRunAsk, 0)
}

/**
 * Every open end, not just the ones this policy would take next — the account's
 * and the export's arithmetic, because they print the same words. Reporting
 * `reachable` alone would have this row and the closing toast say "6 open ends
 * left" about a run whose account says 131.
 */
const openTotal = (o: RunOpen): number => o.reachable + o.pastDepth + o.folded

/**
 * What a finished run found, as the ending's line prints it: "2 held up · 1
 * didn’t survive · 3 still open". The card's own counts (`runFound`) in the
 * card's own words (`FOUND_WORD`), and only the three a run did — no names, no
 * ranking and no figure, which are the card's to print in the thread (D267). A
 * line at nought is left out rather than printed as a target missed — the card's
 * rule for its counts — and a run that found nothing countable prints nothing,
 * leaving its stop head alone. Exported for its test.
 */
export function foundCounts(rows: readonly FoundRow[]): string | null {
  const said = rows
    .filter((r) => r.line === 'heldUp' || r.line === 'didNotSurvive' || r.line === 'open')
    .filter((r) => r.count)
    .map((r) => `${r.count} ${FOUND_WORD[r.line as keyof typeof FOUND_WORD].toLowerCase()}`)
  return said.length ? said.join(' · ') : null
}

/** The channel a verb's dot wears, so the row speaks the canvas's colour language
 *  rather than inventing a second one. */
const VERB_TINT: Partial<Record<string, string>> = {
  challenge: 'var(--sem-tension)',
  experiment: 'var(--sem-gap)',
  verify: 'var(--sem-signal)',
  bridge: 'var(--sem-insight)',
}

/** The whole caveat, on hover, for the figures below. The short forms carry
 *  "est."; this carries why the figure leans the way it does. */
const MONEY_WHY = `An estimate at list prices read on ${PRICE_READ_ON} — each token priced at the rate its kind bills at: a cache read at the read rate, a cache write with its premium, and a page opened for nothing where the provider charges nothing for one. Anything a receipt does not break down is charged at the full rate, and discounts or credits are not counted, so at these prices the real charge is never higher and can be lower. Not a bill.`

/**
 * What a run at rest would draw, as the row prints it after the move count.
 *
 * A cap that binds — under the run's own projection, or over a model with none —
 * is where the run is expected to stop, so it leads: "stops near $4 est.". A cap
 * above the projection is a backstop and the projection leads, with the cap
 * beside it so it is never hidden. No cap, the projection alone; no projection
 * and no cap, nothing — half a figure would lean LOW, the one direction the
 * estimate is never allowed to lean. Exported for its test.
 */
export function restFigure(est: number | null, cap: number | null): string | null {
  if (cap !== null && (est === null || cap < est)) return `stops near ${fmtUsd(cap)} est.`
  if (est === null) return null
  return cap !== null ? `est. ${fmtUsd(est)} · ${fmtUsd(cap)} cap` : `est. ${fmtUsd(est)}`
}

/**
 * The move in flight, as an ordinal of the budget: "Move 5 of 12", or "Moves 5–7
 * of 12" while one pass works several angles. Between passes it names the move
 * being chosen. Clamped to the budget, so the gap after the last move cannot read
 * "Move 13 of 12". The spent count, passes named apart, is `runProgress` — the
 * account's and the thread's own sentence — under Details. Exported for its test.
 */
export function moveOrdinal(movesRun: number, budget: number, packet = 1): string {
  const first = Math.min(budget, movesRun + 1)
  const last = Math.min(budget, movesRun + Math.max(1, packet))
  return last > first ? `Moves ${first}–${last} of ${budget}` : `Move ${first} of ${budget}`
}

/** The live figure, short, for the row. Null for a model with no published rate:
 *  "$0.00" over a run on somebody's own gateway would be the one lie this whole
 *  arrangement exists to avoid. */
export function liveFigure(s: RunLive['spend']): string | null {
  if (!s.priced) return null
  return s.ceiling !== null
    ? `est. ${fmtUsd(s.usd)} of ${fmtUsd(s.ceiling)}`
    : `est. ${fmtUsd(s.usd)} so far`
}

/** …and in full, under Details: the pod's own sentence, "estimated" spelled out. */
const liveMoney = (s: RunLive['spend']): string | null => {
  if (!s.priced) return null
  return s.ceiling !== null
    ? `About ${fmtUsd(s.usd)} of your ${fmtUsd(s.ceiling)} ceiling, estimated`
    : `About ${fmtUsd(s.usd)} so far, estimated`
}

/** What the composer hands the row at rest — every figure already the tune
 *  panel's, so two computations of one press cannot drift. */
export interface RunRest {
  /** A graph to run on. On a blank canvas the row is the dials and Adjust. */
  show: boolean
  runnable: boolean
  /** The press's own sentence, on hover — runnable or not. */
  title: string
  est: number | null
  moves: number
  /** The ceiling in force, the draft's /spend applied; null for none. */
  cap: number | null
  /** No key for the run's passes: said where the figure would be. */
  needsKey: boolean
  /** Why the press cannot run, when that is a fact about the dials — a cap under
   *  the floor (D154) — in words on the row, never only in a tooltip. */
  why: string | null
  onRun: () => void
  /** An opening is going: Run holds the run until it lands (D351). */
  afterOpening?: boolean
  /** The run held for the opening, as the row priced it at the press, and the
   *  press that lets it go. */
  held?: { moves: number; est: number | null; onDrop: () => void } | null
  /** The rest of a run the person stopped, or the provider stopped answering,
   *  which takes the press (`continuationOf`, D337). */
  continuation: {
    moves: number
    est: number | null
    /** What is left of the ended run's ceiling, or null. */
    left: number | null
    runnable: boolean
    title: string
    /** Why it cannot continue, in words — what is left of its cap is under a pass. */
    why: string | null
    onRun: () => void
  } | null
  /** The four dials, summarised for the blank canvas's row. */
  dials: string
}

/**
 * The row. `rest` and `adjust` come from the composer; without them — a phone
 * with its sheet open, where the composer yields and App seats the strip in the
 * session column instead — the row renders only a live or ended run, because Stop
 * must stay on screen while the sheet is up, and there is nothing at rest to say
 * that the composer will not say when the sheet closes.
 */
export function RunStrip(props: { rest?: RunRest; adjust?: RunAdjust }) {
  const run = useStore((s) => s.run)
  return <RunStripView run={run} {...props} />
}

/** The Adjust disclosure the composer hands the strip: whether it is open, the
 *  dials it opens, and the words on its face. */
export interface RunAdjust {
  open: boolean
  panel: ReactNode
  face: string
}

/**
 * The strip as a function of the run's live projection — the store's `run`,
 * handed in rather than read, so a test can render every phase of it: a server
 * render only ever sees the store's initial state.
 */
export function RunStripView({
  run,
  rest,
  adjust,
}: {
  run: RunLive | null
  rest?: RunRest
  adjust?: RunAdjust
}) {
  const live = run !== null && run.phase !== 'ended'
  const ended = run !== null && run.phase === 'ended'

  // A run going live closes the dials: the live row has no Adjust — a live run's
  // moves and cap were fixed at its start, so a dial offered beside Stop would
  // read as steering the run in flight while changing only the next one. The
  // disclosure is module state (`adjust.ts`) because App reads it for the phone's
  // replay dock, so it has to be closed there, not merely left unrendered.
  useEffect(() => {
    if (live) setAdjustOpen(false)
  }, [live])

  if (live && run) return <LiveRow run={run} alone={!rest} />
  if (!rest) return ended && run ? <EndedHead run={run} alone /> : null
  return (
    <div className="runstrip" data-state={ended ? 'ended' : 'rest'}>
      {ended && run && <EndedHead run={run} />}
      {/* Not over a held run: it was fixed at its press, so a dial moved now
          would read as steering it while changing only the run after it. */}
      {adjust?.open && !rest.held && <div className="runstrip-panel">{adjust.panel}</div>}
      <RestLine rest={rest} adjust={adjust} />
    </div>
  )
}

/**
 * At rest: what the next run would do, its press, and Adjust.
 *
 * After a Stop the press offers the rest of that run, priced, and a fresh run
 * steps back to a quiet Start over beside it — a usability session pressed Stop,
 * then Let it run, and got a brand-new run at move 1 with nothing said about the
 * moves the stopped one had left. And after a run the provider stopped answering,
 * whose failed passes are the strip's cause line just above (D337): a rate limit
 * waited out or credits topped up, the rest is still the person's. Only after
 * those two: every other ending is the run's own stop condition, and a press that
 * continued past one would overrule it (`continuationOf`).
 */
function RestLine({ rest, adjust }: { rest: RunRest; adjust?: RunAdjust }) {
  const cont = rest.show ? rest.continuation : null
  // The palette's ask lands on whichever of these is the row's first press
  // (`focusRunPress`): Continue or Run, and Adjust where that one cannot run.
  const goRef = useRef<HTMLButtonElement | null>(null)
  const adjustRef = useRef<HTMLButtonElement | null>(null)
  useEffect(() => {
    const take = () => {
      const go = goRef.current
      const el = go && !go.disabled ? go : adjustRef.current
      // Behind the palette's scrim the stage is still inert, and its focus
      // return has yet to run: not yet.
      if (!el || el.closest('[inert]')) return false
      // Marked as well as focused. A palette row picked with a pointer is
      // followed by a programmatic focus that is not `:focus-visible`, so the
      // ring would show only to a keyboard; the mark shows it to everyone, and
      // goes with the focus.
      el.focus({ focusVisible: true } as FocusOptions)
      if (document.activeElement !== el) return false
      el.dataset.summoned = ''
      el.addEventListener('blur', () => delete el.dataset.summoned, { once: true })
      return true
    }
    runTakers.add(take)
    offerRunAsk()
    return () => {
      runTakers.delete(take)
    }
  }, [])
  const held = rest.show ? (rest.held ?? null) : null
  // Run swapped for Cancel, or back: the press that did it is gone from under
  // the focus, so the one that replaced it takes it — never a focus a reader has
  // since moved somewhere else.
  const swapped = useRef(false)
  useEffect(() => {
    if (!swapped.current) return
    swapped.current = false
    const here = document.activeElement
    if (here && here !== document.body) return
    goRef.current?.focus({ preventScroll: true })
  }, [held !== null])
  const figure = held
    ? restFigure(held.est, rest.cap)
    : cont
      ? restFigure(cont.est, cont.left)
      : rest.needsKey
        ? STATUS_WORD.needsKey
        : restFigure(rest.est, rest.cap)
  // Only the refusals that are facts about the dials get a line; a pass in flight
  // is said by the disabled press, and a missing key by the figure's own slot —
  // and by a line too once the reader holds some key, since a key the run needs
  // is then on another route, and possibly one a dial scheduled (`runKeyGap`).
  const why = cont ? cont.why : rest.show ? rest.why : null
  // The why line describes the presses, so a reader focused on one — the palette
  // lands on Adjust when the press cannot run — hears why without hunting for it.
  const whyId = useId()
  const describedBy = why ? whyId : undefined
  return (
    <>
      <div className="runstrip-line">
        {/* The run's mark at rest (D352): the loop it goes round. A live run's
            line wears the move's own dot in its place. */}
        <Icon name="history" size={15} className="runstrip-icon" />
        {rest.show ? (
          // The figure is its own item in a wrapping lead: beside the words
          // where there is room, under them where there is not — never cut
          // off by the ellipsis that shortens the words, because the figure is
          // the one thing on this line a press must not be made without.
          <span className="runstrip-lead">
            <span className="runstrip-main">
              {held ? (
                <>
                  <b>Runs when the opening lands</b> · <span className="num">{held.moves}</span>{' '}
                  moves
                </>
              ) : cont ? (
                // The count alone, beside the Continue it belongs to: "of the
                // run you stopped" was the half an ellipsis took at the thread's
                // foot, and Continue's own name says it in full — in words that
                // hold after a Stop and after an API that stopped answering.
                <b className="num">
                  {cont.moves} move{cont.moves === 1 ? '' : 's'} left
                </b>
              ) : (
                <>
                  <b>Let it run</b> · <span className="num">{rest.moves}</span> moves
                </>
              )}
            </span>
            {figure && (
              <span className="num runstrip-sub" title={MONEY_WHY}>
                {figure}
              </span>
            )}
          </span>
        ) : (
          // No graph, nothing to run: the dials are what the next seed and the
          // run after it will read, so they stay said — the four chips' faces,
          // in one line.
          <span className="runstrip-lead runstrip-lead--quiet">
            <span className="runstrip-main">{rest.dials}</span>
          </span>
        )}
        <span className="runstrip-acts">
          {/* Adjust first, the press that spends last, at the row's end — the
              owner's design's order (D356). */}
          {adjust && !held && (
            <button
              ref={adjustRef}
              className="runstrip-adjust"
              data-on={adjust.open || undefined}
              aria-expanded={adjust.open}
              aria-describedby={describedBy}
              onClick={() => setAdjustOpen(!adjust.open)}
              title="Lens, temper, moves and the spend cap for the next run — and, on a phone, the replay"
            >
              Adjust
              {adjust.face && <span className="runstrip-adjust-face">{adjust.face}</span>}
              <Icon
                name="chevron"
                size={9}
                // Up at the panel it opens above the row, down at it once open.
                style={{ transform: adjust.open ? 'rotate(90deg)' : 'rotate(-90deg)' }}
              />
            </button>
          )}
          {cont && (
            <>
              <button
                ref={goRef}
                className="btn btn--primary runstrip-go"
                disabled={!cont.runnable}
                onClick={() => {
                  seatOn('stop')
                  cont.onRun()
                }}
                onKeyDown={noHeldPress}
                title={cont.title}
                aria-describedby={describedBy}
                // The visible word first, so a voice user's "click Continue"
                // finds it (label in name).
                aria-label={
                  cont.runnable
                    ? `Continue — pick up where the run left off, ${cont.moves} move${cont.moves === 1 ? '' : 's'} left${
                        cont.est !== null
                          ? `, estimated ${fmtUsd(cont.est)}, leans high, not a limit`
                          : ''
                      }`
                    : `Continue — ${cont.title}`
                }
              >
                <Icon name="play" size={9} />
                Continue
              </button>
              {/* The plain run, demoted rather than removed: a fresh run under
                  the dials is still one press away, it just is not the default
                  where the last run left moves. */}
              <button
                className="btn runstrip-quiet"
                disabled={!rest.runnable}
                onClick={() => {
                  seatOn('stop')
                  rest.onRun()
                }}
                onKeyDown={noHeldPress}
                aria-describedby={describedBy}
                title={
                  rest.runnable
                    ? `A new run from move 1 under the dials, leaving the last one as it is${
                        rest.est !== null
                          ? ` — about ${fmtUsd(rest.est)} at list prices, an estimate that leans high, not a limit`
                          : ''
                      }`
                    : rest.title
                }
              >
                Start over
              </button>
            </>
          )}
          {held && (
            // The held run's one press: let it go. The opening goes on landing.
            <button
              ref={goRef}
              className="btn runstrip-quiet"
              onClick={() => {
                swapped.current = true
                held.onDrop()
              }}
              onKeyDown={noHeldPress}
              aria-describedby={describedBy}
              title="Let the run go. The opening still lands, and Run is offered again."
              aria-label="Cancel the run that waits for the opening"
            >
              Cancel
            </button>
          )}
          {rest.show && !cont && !held && (
            <button
              ref={goRef}
              className="btn btn--primary runstrip-go"
              disabled={!rest.runnable}
              onClick={() => {
                // Under the opening the press is held, and Cancel takes its
                // place; otherwise the live row's Stop does.
                if (rest.afterOpening) swapped.current = true
                else seatOn('stop')
                rest.onRun()
              }}
              onKeyDown={noHeldPress}
              aria-describedby={describedBy}
              title={rest.title}
              aria-label={
                rest.runnable
                  ? `Let it run${rest.afterOpening ? ' when the opening lands' : ''} — ${rest.moves} moves${
                      rest.est !== null
                        ? `, estimated ${fmtUsd(rest.est)}, leans high, not a limit`
                        : ''
                    }`
                  : `Let it run — ${rest.title}`
              }
            >
              <Icon name="play" size={9} />
              Run
            </button>
          )}
        </span>
      </div>
      {why && (
        <p id={whyId} className="runstrip-why">
          {why}
        </p>
      )}
    </>
  )
}

/**
 * How the run ended, above the rest line — or alone, on the phone's strip over
 * an open sheet. Read off the live projection, which is the ending the loop just
 * published; the record says the same thing, but the projection is what a ✕
 * clears, so the head and its dismissal are one piece of state.
 */
function EndedHead({ run, alone = false }: { run: RunLive; alone?: boolean }) {
  const setRun = useStore((s) => s.setRun)
  const setRail = useStore((s) => s.setRail)
  const setRightTab = useStore((s) => s.setRightTab)
  const accountRef = useRef<HTMLButtonElement | null>(null)
  // After the strip's own Stop: the first press of the ending, which spends
  // nothing — never Continue, where a second Enter would start a paid run.
  useSeat('account', accountRef)
  // A run that stopped because the model stopped answering says what the last
  // pass met — the credits, a rate limit, the wire — in that pass's own sentence,
  // off the record (`unreachableWhy`). Under one head those were one ending, and
  // only one of them is fixed by waiting.
  const record = useStore((s) => s.session.runs.find((r) => r.id === run.id))
  const why = unreachableWhy(record)
  // What it found, off the record and the graph, as the card in the thread reads
  // it — with no Next, which is the card's (it runs the policy over the graph).
  // `rev` because a patch can land on a node without a new session object.
  const session = useStore((s) => s.session)
  const rev = useStore((s) => s.rev)
  const found = useMemo(
    () => (record?.moves.length ? foundCounts(runFound(record, session, null)) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [record, session, rev],
  )
  // The run's ending is its card, so the press goes there when there is one: the
  // stop reason alone ended a run on why it stopped, and the account was its one
  // press — the ledger, where what held up and what broke sat one hidden pane
  // away (D335). The card's last row is the account, so the ledger is one press
  // further. A run with no moves has no card (`RunFound`'s own rule), and keeps
  // the account.
  const toCard = !!record?.moves.length
  const head = (
    <div className="runstrip-head">
      <i aria-hidden />
      <span className="runstrip-lead" role="status" aria-live="polite">
        {run.stop
          ? RUN_STOP_HEAD[run.stop]
          : runProgress(run.movesRun, run.budget.moves, run.passesRun)}
        {found && <span className="num runstrip-found">{found}</span>}
      </span>
      <span className="runstrip-acts">
        <button
          ref={accountRef}
          className="linkish runstrip-account"
          title={
            toCard
              ? 'What held up, what didn’t survive and what is still open, in the run’s message in the thread. Its last row is the account.'
              : 'Everything the run did, move by move.'
          }
          onClick={() => {
            if (toCard) return openRunFound(run.id)
            setRail(true)
            setRightTab('run')
          }}
        >
          {toCard ? 'What it found' : 'See the account'}
        </button>
        {/* Clears the ending and nothing else. The bar this replaced also put
            the coach away, because it carried the coach's count; the count has
            its own line now, with its own ✕, and Escape still takes the two one
            rung at a time. */}
        <button
          className="runstrip-close"
          aria-label="Put the ending away"
          title="Puts this away. The account stays in the Runs tab."
          onClick={() => setRun(null)}
        >
          <Icon name="close" size={9} />
        </button>
      </span>
      {why && <span className="runstrip-cause">{why}</span>}
    </div>
  )
  return alone ? (
    <div className="runstrip runstrip--alone panel" data-state="ended">
      {head}
    </div>
  ) : (
    head
  )
}

/**
 * A run while it runs. The row is the steering surface, which is why the run
 * releases its lock between passes rather than holding it: Stop ends it, and at
 * an ask-first pause Go and Not there answer it. None of them blocks a pass on a
 * countdown, so a run stays something you can walk away from.
 */
function LiveRow({ run, alone }: { run: RunLive; alone: boolean }) {
  const pinned = useStore((s) => s.viewPinned)
  const unpin = useStore((s) => s.unpinView)
  const modelId = useStore((s) => s.modelId)
  const keys = useStore((s) => s.keys)
  const customBase = useStore((s) => s.customBase)
  const customModel = useStore((s) => s.customModel)
  const roleModels = useStore((s) => s.roleModels)
  const session = useStore((s) => s.session)
  const detailsId = useId()
  // After Run, Continue or Start over: the one Stop, where the pressed button was.
  const stopRef = useRef<HTMLButtonElement | null>(null)
  useSeat('stop', stopRef)
  // Per run: a new run opens with its details folded, whatever the last one did.
  const [more, setMore] = useState<string | null>(null)
  const open = more === run.id
  const now = run.now
  const waiting = run.phase === 'waiting' && now !== null
  // Resolved as the thread's gate resolves it — `auditorFor`, the resolution the
  // pass itself makes — and only while an audit is the proposal on the table.
  const auditor = useMemo(
    () =>
      waiting && now?.kind === 'audit'
        ? auditorFor({ modelId, keys, customBase, customModel, session }, now.nodeId)
        : null,
    [waiting, now, modelId, keys, customBase, customModel, session],
  )
  // The proposed move's own route, never the selection's key: a pause priced a
  // routed check the selection's key could not run, and said "Needs a key" over
  // a move whose route had one (`gateReady`).
  const ready =
    now !== null &&
    gateReady(
      { modelId, roleModels, keys, customBase, customModel },
      now.kind,
      auditor?.entry ?? null,
    )
  const figure = liveFigure(run.spend)
  const packet = now?.packet && now.packet.length > 1 ? now.packet.length : 1
  // Between two moves the run is choosing — or, after a move that failed on the
  // API's side or the wire's, waiting out what it met, and saying how long.
  const pause = now ? null : (run.pause ?? null)
  const what = now
    ? `${packet > 1 ? `${packet} angles in one pass` : OP_LABEL[now.kind]}${now.label ? ` · ${now.label}` : ''}`
    : STATUS_WORD.choosingMove
  const c = run.counts
  const money = liveMoney(run.spend)

  return (
    <div
      className={alone ? 'runstrip runstrip--alone panel' : 'runstrip'}
      data-state={waiting ? 'waiting' : 'live'}
    >
      {open && (
        <div className="runstrip-panel runstrip-details" id={detailsId}>
          {/* The stored reason, in the thread's own sentence — the string the
              account and the export print, so no surface can describe one move
              two ways. */}
          <p className="runstrip-detail">
            {now ? (
              <>
                <b>{what}</b>
                <span>It went here because {MOVE_REASON[now.why]}.</span>
              </>
            ) : (
              <b>{what}</b>
            )}
          </p>
          {/* Moves spent, passes named apart: `runProgress`, the thread row's and
              the account's own sentence, so the surfaces watching one run cannot
              count it two ways. */}
          <p className="gnote num">
            {runProgress(run.movesRun, run.budget.moves, run.passesRun)} · {c.nodes} node
            {c.nodes === 1 ? '' : 's'}
            {/* The endings' own words, never "dead ends": the count takes in the
                questions reasoning cannot settle, which are not dead ends. */}
            {c.dead > 0 && ` · ${runEndingsLine(c, 'strip')}`}
            {` · ${openTotal(run.open)} open end${openTotal(run.open) === 1 ? '' : 's'}`}
          </p>
          {money && (
            <p className="gnote" title={MONEY_WHY}>
              {money}
            </p>
          )}
          {run.pause && !now && <p className="gnote">{runPauseWhy(run.pause)}</p>}
          {run.capNote && <p className="gnote">{run.capNote}</p>}
          {pinned && (
            <p className="gnote">
              Following stopped when you moved the canvas.{' '}
              <button className="linkish" onClick={unpin}>
                Follow again
              </button>
            </p>
          )}
          {/* Between pauses "Not there" lives here; at a pause it is on the row,
              beside the Go it answers. The same press either way. */}
          {!waiting && (
            <p className="gnote">
              <button
                className="btn btn--outline runstrip-decline"
                disabled={!now?.nodeId}
                onClick={declineHere}
                title="Closes this branch for the rest of the run. What is already on the canvas stays."
              >
                Not there
              </button>{' '}
              Fold a branch away (H) and the run stops going there.
            </p>
          )}
        </div>
      )}
      <div className="runstrip-line">
        <i
          className="runstrip-dot"
          aria-hidden
          style={{ background: now ? (VERB_TINT[now.kind] ?? 'var(--accent)') : 'var(--text-3)' }}
        />
        <span className="runstrip-lead runstrip-lead--live">
          {/* The one live region: the move in flight, never blanking between
              passes — a line that empties for four seconds every minute reads as
              a stall rather than as a gap. */}
          <span className="runstrip-main" role="status" aria-live="polite">
            <b className="num">{moveOrdinal(run.movesRun, run.budget.moves, packet)}</b> ·{' '}
            {pause ? (
              <>
                {/* The seconds tick once a second, and in here each tick was
                    read aloud; the region says the wait once (`runPauseSpoken`). */}
                <span aria-hidden="true">{runPauseLine(pause)}</span>
                <span className="sr-only">{runPauseSpoken(pause)}</span>
              </>
            ) : (
              what
            )}
          </span>
          {/* Under the move rather than beside it, so the presses on the right
              never push the move's name down to three words. */}
          {figure && (
            <span className="num runstrip-sub" title={MONEY_WHY}>
              {figure}
            </span>
          )}
        </span>
        <span className="runstrip-acts">
          <button
            className="runstrip-more"
            aria-expanded={open}
            aria-controls={open ? detailsId : undefined}
            aria-label="Details"
            onClick={() => setMore(open ? null : run.id)}
            title="Why this move, what the run has done so far, and Not there"
          >
            <span className="runstrip-more-word">Details</span>
            <Icon
              name="chevron"
              size={9}
              style={{ transform: open ? 'rotate(90deg)' : 'rotate(-90deg)' }}
            />
          </button>
          {/* Where it always is, whatever the phase: a Stop that moved when the
              gate arrived would be under the thumb that reached for it a second
              earlier, pressing something else. */}
          <button
            ref={stopRef}
            className="btn btn--outline runstrip-stop"
            onClick={() => {
              seatOn('account')
              stopRun()
            }}
            title="Stops the run. The pass in flight is cancelled; whatever it had already delivered is kept. Space stops it too."
            aria-label="Stop the run"
          >
            <Icon name="stop" size={13} />
            Stop
          </button>
        </span>
      </div>
      {/* An ask-first pause: the gate on its own line under the move it asks
          about — the word, the figure, Go and Not there — so the question reads
          as one sentence and the live line above keeps its shape. */}
      {waiting && now && (
        <div className="runstrip-gate">
          <span className="runstrip-tag">{STATUS_WORD.awaitingGo}</span>
          {/* The figure BESIDE the press: with the thread on another tab this
              strip is the only approval surface on screen, and an unpriced
              billable press is the shape the armed-chip discipline forbids. */}
          {ready ? (
            <VerbPrice
              verb={gatePriceVerb(now.kind)}
              auditor={now.kind === 'audit' ? (auditor?.entry ?? null) : undefined}
              inRun
            />
          ) : (
            <span className="vp-spent">{STATUS_WORD.needsKey}</span>
          )}
          <span className="runstrip-gate-acts">
            <button
              className="btn btn--primary runstrip-go"
              onClick={approveMove}
              title="Runs the proposed move. Ask-first holds here between passes; nothing is spent while it waits."
              // Named for the move it runs — "Go" is all the strip has room to
              // print. The visible word leads, so a voice-control user can say it.
              aria-label={`Go: ${OP_LABEL[now.kind]}${now.label ? ` on “${now.label}”` : ''}`}
            >
              Go
            </button>
            <button
              className="btn runstrip-quiet"
              disabled={!now.nodeId}
              onClick={declineHere}
              title="Closes this branch for the rest of the run and asks about another end."
            >
              Not there
            </button>
          </span>
        </div>
      )}
    </div>
  )
}
